import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";

interface BulkActionBarProps {
  selectedCount: number;
  totalCount: number;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onDeleteSelected: () => void;
  isDeleting?: boolean;
  className?: string;
}

export const BulkActionBar = ({
  selectedCount,
  totalCount,
  onSelectAll,
  onDeselectAll,
  onDeleteSelected,
  isDeleting = false,
  className,
}: BulkActionBarProps) => {
  const allSelected = selectedCount === totalCount && totalCount > 0;
  const someSelected = selectedCount > 0 && selectedCount < totalCount;

  return (
    <div
      className={cn(
        "flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border",
        className
      )}
    >
      <div className="flex items-center gap-3">
        <Checkbox
          checked={allSelected}
          ref={(ref) => {
            if (ref) {
              (ref as HTMLButtonElement & { indeterminate?: boolean }).indeterminate = someSelected;
            }
          }}
          onCheckedChange={(checked) => {
            if (checked) {
              onSelectAll();
            } else {
              onDeselectAll();
            }
          }}
          aria-label="Select all"
        />
        <span className="text-sm text-muted-foreground">
          {selectedCount > 0
            ? `${selectedCount} of ${totalCount} selected`
            : `${totalCount} items`}
        </span>
      </div>

      {selectedCount > 0 && (
        <div className="flex items-center gap-2">
          <Button
            variant="destructive"
            size="sm"
            onClick={onDeleteSelected}
            disabled={isDeleting}
            className="flex items-center gap-2"
          >
            <Icon name="delete" size={14} />
            {isDeleting ? "Deleting..." : `Delete (${selectedCount})`}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onDeselectAll}
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
};
